import { Drawer } from '@liveauctioneers/hammer-ui-core/drawer';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Bell, SlidersHorizontal } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CategoryAd } from '@/components/Ads/CategoryAd';
import ManageButton from '@/components/Button/ManageButton';
import { DismissableGetMobileBannerAlt } from '@/components/DismissableGetMobileBanner/DismissableGetMobileBannerAlt';
import PageTitle from '@/components/PageTitle/PageTitle';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import MyFavoritesSlideMenu from '@/pages/MyItems/MyFavorites/components/MyFavoritesSlideMenu/MyFavoritesSlideMenu';
import { useAppSelector } from '@/redux/hooks';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { getInitialWidth } from '@/redux/modules/windowSize';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getMyBidsUrl, getNotificationsUrl, getSavedItemsUrl } from '@/utils/urls';

import { MyItemsTab } from '../../types/MyItemsPage.types';

type Props = {
    children: ReactNode;
    tab: MyItemsTab;
};

/**
 * The "my items" page is actually two different pages: "my favorites" and "my bids"
 * where the routing to either page is controlled by tabs
 */
const MyItemsPageContainer = ({ children, tab }: Props) => {
    const ssrWidth = useAppSelector(getInitialWidth);
    const isDesktopView = useIsWiderThan(990, ssrWidth);
    const navigate = useNavigate();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const formatMessage = useAppFormatMessage();

    // 'favorites-page' is unfortunately the ad id
    const adId = tab === MyItemsTab.MyFavorites ? 'favorites-page' : MyItemsTab.MyBids;
    const manageUrl = getNotificationsUrl('searches');

    /**
     * Navigate to the my-bids or my-favorites via tabs
     */
    const handleTabsOnChange = (contentId: string) => {
        if (contentId === MyItemsTab.MyBids && tab !== MyItemsTab.MyBids) {
            navigate(getMyBidsUrl());
        } else if (contentId === MyItemsTab.MyFavorites && tab !== MyItemsTab.MyFavorites) {
            navigate(getSavedItemsUrl());
        }
    };

    const adPageName = `leaderboard-${adId}`;

    return (
        <>
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
            />
            <div className="relative mx-auto flex w-full max-w-[1240px] flex-col self-center px-sm">
                <PageTitle
                    className="my-lg"
                    title={<>{formatMessage(['myItems', 'title'])}</>}
                />
                <DismissableGetMobileBannerAlt className="mb-md" />
                <div className="relative">
                    <Tabs
                        currentTab={tab}
                        onTabChanged={handleTabsOnChange}
                        tabs={[
                            {
                                content: tab === MyItemsTab.MyFavorites ? children : null,
                                contentId: MyItemsTab.MyFavorites,
                                label: formatMessage(['myItems', 'favorites']),
                            },
                            {
                                content: tab === MyItemsTab.MyBids ? children : null,
                                contentId: MyItemsTab.MyBids,
                                label: formatMessage(['myItems', 'bids']),
                            },
                        ]}
                    />
                    {isDesktopView && (
                        <div className="absolute top-[-7px] right-0 flex items-center">
                            <ManageButton
                                hideManage
                                id="manageNotifications"
                                messageId="manageNotifications"
                                to={manageUrl}
                                withPopover
                            />
                        </div>
                    )}
                    {!isDesktopView && tab === MyItemsTab.MyFavorites && (
                        <div className="absolute top-[-7px] right-0 flex items-center">
                            <IconButton
                                icon={SlidersHorizontal}
                                onClick={() => setIsDrawerOpen(true)}
                            />
                            <Drawer
                                actions={
                                    <SubtleMonochromeLink
                                        href={manageUrl}
                                        id="manageNotifications"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsDrawerOpen(false);
                                            window.requestAnimationFrame(() => {
                                                navigate(manageUrl);
                                            });
                                        }}
                                    >
                                        <InlineButton
                                            icon={Bell}
                                            size="sm"
                                            style="neutral"
                                        >
                                            {formatMessage(['manageNotifications'])}
                                        </InlineButton>
                                    </SubtleMonochromeLink>
                                }
                                anchor="right"
                                closeOnOutsideClick
                                density="comfortable"
                                handleClose={() => setIsDrawerOpen(false)}
                                hasShadow
                                headerText={formatMessage(['favorites', 'favorite_items']) as unknown as string}
                                show={isDrawerOpen}
                                size="narrow"
                            >
                                <MyFavoritesSlideMenu />
                            </Drawer>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default MyItemsPageContainer;
